import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Column, ColumnBodyOptions } from 'primereact/column'
import { Menu } from 'primereact/menu'
import { MenuItem } from 'primereact/menuitem'
import { TreeNode } from 'primereact/treenode'
import {
  TreeTableEvent,
  TreeTableSelectionKeysType,
} from 'primereact/treetable'

import {
  BooleanBodyTemplate,
  LabelBodyTemplate,
  ValueWithUnitBodyTemplate,
} from '../../../../../components/tableTemplates'
import {
  DeliveryForecastDespatchAdvicePackagingItem,
  DeliveryForecastDespatchAdvicePackagingMaterial,
} from '../../../../../data/models'
import { ConfigurableTreeTable } from '../../../../../utils/wrapper'
import { InfoPackagingData } from '../../../../selfservice/master-data-management/packaging-data/components/dialogs/Info/InfoPackagingData'
import { useAllPackagingMaterial } from '../../../../selfservice/master-data-management/packaging-data/data'
import { useConfigContext } from '../../../../ui-config'
import {
  useAllDeliveryForecastDespatchAdviceItemPackagingItems,
  useDeliveryForecastDespatchAdviceItemPackagingById,
} from '../../../data/hooks'
import { PackagingNode, PackagingNodeWithBatches } from '../../../data/model'
import { getParentOptions } from '../../../utils'
import { SummaryShippingTable } from '../SummaryShippingTable'
import { AddData } from './dialogs/Add'
import { DeleteData } from './dialogs/Delete'
import { EditData } from './dialogs/Edit'
import { Info } from './dialogs/Info'

type Props = {
  despatchAdviceId: string
  itemId: string
  packagingId: string
}

export const PackagingTable: React.FC<Props> = ({
  despatchAdviceId,
  itemId,
  packagingId,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const menuRef = useRef<Menu>(null)

  const { data: itemPackagings } =
    useDeliveryForecastDespatchAdviceItemPackagingById(
      despatchAdviceId,
      itemId,
      packagingId,
    )
  const { data: packagingMaterialData } = useAllPackagingMaterial()
  const { data, error, isLoading } =
    useAllDeliveryForecastDespatchAdviceItemPackagingItems(
      despatchAdviceId,
      itemId,
      packagingId,
    )

  const [selectedNodeKeys, setSelectedNodeKeys] =
    useState<TreeTableSelectionKeysType>({})
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const [isDeleteRequestRunning, setIsDeleteRequestRunning] =
    useState<boolean>(false)

  // Dialog state
  const [dialogState, setDialogState] = useState({
    add: {
      isVisible: false,
      label: null as
        | DeliveryForecastDespatchAdvicePackagingItem['PackagingType']
        | null,
    },
    packagingInfo: {
      isVisible: false,
      data: null as DeliveryForecastDespatchAdvicePackagingMaterial | null,
    },
    delete: {
      isVisible: false,
      ids: [] as string[],
    },
    info: {
      isVisible: false,
      id: null as string | null,
    },
    edit: {
      isVisible: false,
      id: null as string | null,
    },
  })

  const handleDialogShow = useCallback(
    (
      type: 'add' | 'packagingInfo' | 'delete' | 'info' | 'edit',
      data: any = null,
    ) => {
      setDialogState(prevState => ({
        ...prevState,
        [type]: { ...prevState[type], isVisible: true, ...data },
      }))
    },
    [],
  )

  const handleDialogHide = useCallback(
    (type: 'add' | 'packagingInfo' | 'delete' | 'info' | 'edit') => {
      setDialogState(prevState => ({
        ...prevState,
        [type]: {
          ...prevState[type],
          isVisible: false,
          label: null,
          data: null,
          ids: [],
          id: null,
        },
      }))
    },
    [],
  )

  // Helper to add an ID to an array without duplicates
  const addToSet = (array: string[], id: string): string[] => {
    return Array.from(new Set([...array, id])) // Use a Set to ensure uniqueness
  }

  const handleSelect = (event: TreeTableEvent) => {
    const newSelectedId = event.node.data.Id
    setSelectedIds(prevIds => addToSet(prevIds, newSelectedId))
  }

  const handleUnSelect = (event: TreeTableEvent) => {
    const unSelectedId = event.node.data.Id
    const isParentNode = event.node.key === unSelectedId

    if (isParentNode) {
      deselectChildNodes(unSelectedId)
    }

    // Remove the unselected node itself from the selectedIds array
    setSelectedIds(prevIds => prevIds.filter(id => id !== unSelectedId))
  }

  const deselectChildNodes = (parentId: string) => {
    Object.keys(selectedNodeKeys).forEach(key => {
      if (key.includes(parentId)) {
        const childId = key.split(`${parentId}-`)[1]
        setSelectedIds(prevIds => prevIds.filter(id => id !== childId))
      }
    })
  }

  // Table components
  const hasHelperParents =
    getParentOptions(itemPackagings?.Items!, 'Helper').length > 0

  const items: MenuItem[] = [
    {
      label: t(
        'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.items.master.label',
      ),
      command: () => {
        handleDialogShow('add', { label: 'Master' })
      },
    },
    {
      label: t(
        'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.items.single.label',
      ),
      command: () => {
        handleDialogShow('add', { label: 'Single' })
      },
    },
    {
      label: t(
        'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.items.packagingAid.label',
      ),
      command: () => {
        handleDialogShow('add', { label: 'Helper' })
      },
      disabled: !hasHelperParents,
    },
  ]

  const headerActions = (
    <>
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.delete.label',
        )}
        onClick={() => handleDialogShow('delete', { ids: selectedIds })}
        icon="pi pi-trash"
        outlined
        disabled={selectedIds.length === 0}
        loading={isDeleteRequestRunning}
      />

      <Button
        label={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.label',
        )}
        icon="pi pi-plus"
        onClick={event => menuRef.current?.toggle(event)}
        visible={config.deliveryForecast.addSplitItem.enabled !== false}
        className="p-button-outlined"
      />
      <Menu
        model={items}
        popup
        ref={menuRef}
        id="add_packaging"
        className="bg-white"
      />
    </>
  )

  const actionTemplate = (
    data: PackagingNodeWithBatches,
    options: ColumnBodyOptions,
  ) => {
    if (data.data?.PackagingType === 'Batch') return <></>

    return (
      <div className="flex flex-nowrap gap-1">
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          severity="danger"
          onClick={() => handleDialogShow('delete', { ids: [data.data?.Id] })}
        />

        <Button
          icon="pi pi-info-circle"
          visible={config.deliveryForecast.addSplitItem.enabled !== false}
          className="p-button-rounded p-button-text"
          onClick={() => handleDialogShow('info', { id: data.data?.Id })}
        />

        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          visible={config.deliveryForecast.addSplitItem.enabled !== false}
          onClick={() => handleDialogShow('edit', { id: data.data?.Id })}
        />
      </div>
    )
  }

  const PackagingDescriptionBodyTemplate = (data: PackagingNodeWithBatches) => {
    if (
      data.data?.PackagingType === 'Master' ||
      data.data?.PackagingType === 'Single' ||
      data.data?.PackagingType === 'Helper'
    ) {
      const packagingData = data.data.PackagingData

      return (
        <span
          className="text-primary cursor-pointer"
          onClick={() =>
            handleDialogShow('packagingInfo', { data: packagingData })
          }
        >
          {data.data.Description}
        </span>
      )
    }

    return <></>
  }

  // CSS for highlighting the article rows
  const rowClassName = (data: TreeNode) => {
    return {
      'shipping-details-batch-row p-highlight':
        data.data?.PackagingType === 'Batch',
    }
  }

  return (
    <>
      {dialogState.add.label && (
        <AddData
          despatchAdviceId={despatchAdviceId}
          itemId={itemId}
          packagingId={packagingId}
          label={dialogState.add.label!}
          isVisible={dialogState.add.isVisible}
          onHide={() => handleDialogHide('add')}
        />
      )}

      <InfoPackagingData
        id={dialogState.packagingInfo.data?.Id}
        isVisible={dialogState.packagingInfo.isVisible}
        onHide={() => handleDialogHide('packagingInfo')}
      />

      <DeleteData
        despatchAdviceId={despatchAdviceId}
        itemId={itemId}
        packagingId={packagingId}
        ids={dialogState.delete.ids}
        setIds={setSelectedIds}
        keys={selectedNodeKeys}
        setKeys={setSelectedNodeKeys}
        setIsRequestRunning={setIsDeleteRequestRunning}
        isVisible={dialogState.delete.isVisible}
        onHide={() => handleDialogHide('delete')}
      />

      <Info
        despatchAdviceId={despatchAdviceId}
        itemId={itemId}
        packagingId={packagingId}
        id={dialogState.info.id!}
        isVisible={dialogState.info.isVisible}
        onHide={() => handleDialogHide('info')}
      />

      <EditData
        despatchAdviceId={despatchAdviceId}
        itemId={itemId}
        packagingId={packagingId}
        id={dialogState.edit.id}
        isVisible={dialogState.edit.isVisible}
        onHide={() => handleDialogHide('edit')}
      />

      <ConfigurableTreeTable
        config={
          config.deliveryForecastDespatchAdvice.shippingDetails.packagingTable
        }
        value={transformData(data, packagingMaterialData)}
        headerActions={headerActions}
        selectionMode="checkbox"
        selectionKeys={selectedNodeKeys}
        onSelectionChange={e => setSelectedNodeKeys(e.value)}
        onSelect={handleSelect}
        onUnselect={handleUnSelect}
        propagateSelectionUp={false}
        sortField="Position"
        sortOrder={1}
        showGridlines
        rowClassName={rowClassName}
      >
        <Column
          key="packaging-table-position"
          field="Position"
          expander
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-packaging-type"
          field="PackagingType"
          body={(data: PackagingNodeWithBatches) =>
            LabelBodyTemplate(data.data?.PackagingType)
          }
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-packaging-id"
          field="PackagingId"
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-packaging-data-description"
          field="PackagingData.Description"
          body={PackagingDescriptionBodyTemplate}
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-hu-number"
          field="HuNumber"
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-is-outer-packaging"
          field="IsOuterPackaging"
          body={(data: PackagingNodeWithBatches) => {
            if (
              data.data?.PackagingType === 'Master' ||
              data.data?.PackagingType === 'Single' ||
              data.data?.PackagingType === 'Helper'
            ) {
              return BooleanBodyTemplate(data.data?.IsOuterPackaging)
            }
          }}
          align="center"
          alignHeader="center"
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-packaging-data-tare-weight-walue"
          field="PackagingData.TareWeight.Value"
          body={data =>
            ValueWithUnitBodyTemplate(
              data.data?.PackagingData?.TareWeight?.Value,
              data.data?.PackagingData?.TareWeight?.Unit,
            )
          }
          align="right"
          alignHeader="right"
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-batches"
          field="Batchnumber"
          header="Charge"
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-quantity"
          field="Quantity.Value"
          body={data => {
            return ValueWithUnitBodyTemplate(
              data.data?.Quantity?.Value,
              data.data?.Quantity?.Unit,
              0,
            )
          }}
          align="right"
          alignHeader="right"
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-weight-value"
          field="Weight.Value"
          body={data => {
            return ValueWithUnitBodyTemplate(
              data.data?.Weight?.Value,
              data.data?.Weight?.Unit,
            )
          }}
          align="right"
          alignHeader="right"
          className="break-words"
          style={{ width: '150px' }}
        ></Column>
        <Column
          key="packaging-table-actions"
          body={actionTemplate}
          align="right"
          className="sticky right-0	bg-white"
          style={{ width: '150px' }}
        ></Column>
      </ConfigurableTreeTable>

      <SummaryShippingTable
        despatchAdviceId={despatchAdviceId}
        itemId={itemId}
        packagingId={packagingId}
      />
    </>
  )
}

const transformData = (
  data: PackagingNode[] = [],
  packagingMaterialData: DeliveryForecastDespatchAdvicePackagingMaterial[] = [],
): PackagingNodeWithBatches[] | [] => {
  if (data.length === 0 || packagingMaterialData.length === 0) return []

  const createBatchNodes = (
    batches: DeliveryForecastDespatchAdvicePackagingItem['Batches'],
    singleKey: string,
    position: string,
  ): PackagingNodeWithBatches['children'] => {
    return batches?.map((batch, index) => ({
      key: `${singleKey}-${batch.Batchnumber}`,
      data: {
        Id: batch.Batchnumber,
        PackagingType: 'Batch',
        Batchnumber: batch.Batchnumber,
        Position: `${position}.${index + 1}`, // Add position to the batches so it is directly under a single because of table sorting
        Quantity: {
          Value: batch.Quantity,
          Unit: batch.Unit,
        },
      },
    }))
  }

  // Iterating through each item in the data array
  return data.map(item => {
    const newItem = { ...item } as PackagingNodeWithBatches

    // Change wrong PackagingData.Id to the correct one
    if (
      newItem.data?.PackagingType === 'Master' ||
      newItem.data?.PackagingType === 'Single' ||
      newItem.data?.PackagingType === 'Helper'
    ) {
      const foundPackaging = packagingMaterialData?.find(
        f => f.PackagingDataId === item.data?.PackagingId,
      )

      newItem.data.PackagingData.Id = foundPackaging ? foundPackaging.Id : ''
    }

    // Add batches to the table under single packaging
    if (
      newItem.data?.PackagingType === 'Single' &&
      (
        (newItem.data as DeliveryForecastDespatchAdvicePackagingItem).Batches ||
        []
      ).length > 0
    ) {
      const single = newItem.data as DeliveryForecastDespatchAdvicePackagingItem

      const newChildren =
        createBatchNodes(single.Batches, newItem.key!, newItem.data.Position) ||
        []

      // A single can have batches and helpers so we need to copy them too
      if (newItem.children && newItem.children.length > 0) {
        // Batches and helpers
        newItem.children = [...newChildren, ...newItem.children]
      } else {
        // Only batches
        newItem.children = newChildren
      }
    }

    // Transform nested children if they exist
    if (newItem.children && newItem.children.length > 0) {
      newItem.children = transformData(
        newItem.children as PackagingNode[],
        packagingMaterialData,
      )
    }

    return newItem as PackagingNodeWithBatches
  })
}
